import * as yup from "yup";
import { enums } from "../constants/enum";

const commonSchema = {
  brand: yup.string().required("Brand is required"),
  itemName: yup.string().required("Item Name is required"),
  category: yup
    .string()
    .oneOf(Object.values(enums.ITEM_CATEGORIES), "Invalid category")
    .required("Category is required"),
  purchasePrice: yup
    .number()
    .typeError("Purchase Price must be a number")
    .positive("Purchase Price must be greater than 0")
    .required("Purchase Price is required"),
  sellingPrice: yup
    .number()
    .typeError("Selling Price must be a number")
    .positive("Selling Price must be greater than 0")
    .required("Selling Price is required"),
  warrantyPeriodMonths: yup
    .number()
    .typeError("Warranty Period must be a number")
    .min(0, "Warranty Period must be at least 0 months")
    .required("Warranty Period is required"),
  stock: yup.object().shape({
    stockStatus: yup
      .string()
      .oneOf(Object.values(enums.STOCK_STATUS), "Invalid stock status")
      .required("Stock Status is required"),
    storeRoomStock: yup.number().when("stockStatus", {
      is: enums.STOCK_STATUS.READY,
      then: (schema) =>
        schema
          .typeError("Storeroom Stock must be a number")
          .min(0, "Storeroom Stock must be at least 0")
          .required("Storeroom Stock is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    displayStock: yup.number().when("stockStatus", {
      is: enums.STOCK_STATUS.READY,
      then: (schema) =>
        schema
          .typeError("Showroom Stock must be a number")
          .min(0, "Showroom Stock must be at least 0")
          .required("Showroom Stock is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  }),
};

const lenseAttributesSchema = yup.object().shape({
  lense_type: yup
    .string()
    .oneOf(Object.values(enums.LENSE_TYPES), "Invalid lense type")
    .required("Lense type is required"),
  seg: yup.string().nullable().default(null),
  power_range: yup
    .object()
    .shape({
      sph: yup
        .number()
        .min(-25, "SPH must be at least -25")
        .max(25, "SPH must be at most 25")
        .typeError("SPH must be a number")
        .required("SPH is required"),
      cyl: yup
        .number()
        .min(-10, "CYL must be at least -10")
        .max(0, "CYL must be at most 0")
        .typeError("CYL must be a number")
        .required("CYL is required"),
      add: yup
        .number()
        .min(0, "ADD must be at least 0")
        .max(5, "ADD must be at most 5")
        .typeError("ADD must be a number")
        .required("ADD is required"),
    })
    .required("Power range details are required"),
  coating: yup
    .string()
    .trim("Coating must not contain leading or trailing spaces")
    .oneOf(Object.values(enums.LENSE_COATINGS), "Invalid coating")
    .required("Coating is required"),
  transition: yup.mixed().when("coating", {
    is: enums.LENSE_COATINGS.PHOTO_GREY,
    then: () =>
      yup
        .number()
        .typeError("Transition level must be a number")
        .min(1, "Transition level must be at least 1")
        .max(4, "Transition level must be at most 4")
        .integer("Transition level must be an integer")
        .required("Transition level is required for PHOTO_GREY coating"),
    otherwise: (schema) =>
      schema
        .nullable()
        .oneOf(
          [null],
          "Transition level must be null for coatings other than PHOTO_GREY",
        ),
  }),
  manufacture_country: yup
    .string()
    .trim("Manufacture country must not contain leading or trailing spaces")
    .required("Manufacture Country is required"),
  thickness: yup
    .number()
    .min(0, "Thickness must be at least 0")
    .typeError("Thickness must be a number")
    .required("Thickness is required"),
  material: yup
    .string()
    .trim("Material must not contain leading or trailing spaces")
    .required("Material is required"),
});

const frameAttributesSchema = yup.object().shape({
  size: yup.object().shape({
    lens_diagonal: yup
      .number()
      .typeError("Lens Diagonal must be a number")
      .positive("Lens Diagonal must be positive")
      .required("Lens Diagonal is required"),
    bridge: yup
      .number()
      .typeError("Bridge must be a number")
      .positive("Bridge must be positive")
      .required("Bridge is required"),
    arm_length: yup
      .number()
      .typeError("Arm Length must be a number")
      .positive("Arm Length must be positive")
      .required("Arm Length is required"),
  }),
  material: yup.string().required("Material is required"),
  color: yup.string().required("Color is required"),
  model_number: yup.string().required("Model Number is required"),
});

const sunglassAttributesSchema = yup.object().shape({
  size: yup.object().shape({
    lens_diagonal: yup
      .number()
      .typeError("Lens Diagonal must be a number")
      .positive("Lens Diagonal must be positive")
      .required("Lens Diagonal is required"),
    bridge: yup
      .number()
      .typeError("Bridge must be a number")
      .positive("Bridge must be positive")
      .required("Bridge is required"),
    arm_length: yup
      .number()
      .typeError("Arm Length must be a number")
      .positive("Arm Length must be positive")
      .required("Arm Length is required"),
  }),
  frame_material: yup.string().required("Frame Material is required"),
  frame_color: yup.string().required("Frame Color is required"),
  lens_material: yup.string().required("Lens Material is required"),
  lens_color: yup.string().required("Lens Color is required"),
  model_number: yup.string().required("Model Number is required"),
  shape: yup.string().required("Shape is required"),
  lens_type: yup.string().required("Lens Type is required"),
  uv_protection: yup
    .boolean()
    .typeError("UV Protection must be true or false")
    .required("UV Protection is required"),
  gender: yup
    .string()
    .oneOf(Object.values(enums.GENDER), "Invalid gender")
    .required("Gender is required"),
});

const accessoriesAttributesSchema = yup.object().shape({
  type: yup.string().required("Type is required"),
  material: yup.string().required("Material is required"),
  color: yup.string().required("Color is required"),
});

export const itemSchema = yup.object().shape({
  ...commonSchema,
  attributes: yup.mixed().when("category", {
    is: enums.ITEM_CATEGORIES.LENSE,
    then: () => lenseAttributesSchema,
    otherwise: (schema) =>
      schema.when("category", {
        is: enums.ITEM_CATEGORIES.FRAME,
        then: () => frameAttributesSchema,
        otherwise: (schema) =>
          schema.when("category", {
            is: enums.ITEM_CATEGORIES.SUNGLASS,
            then: () => sunglassAttributesSchema,
            otherwise: (schema) =>
              schema.when("category", {
                is: enums.ITEM_CATEGORIES.ACCESSORIES,
                then: () => accessoriesAttributesSchema,
                otherwise: (schema) => schema.notRequired(),
              }),
          }),
      }),
  }),
});

const commonItemUpdateSchema = {
  brand: yup.string().required("Brand is required"),
  itemName: yup.string().required("Item Name is required"),
  category: yup
    .string()
    .oneOf(Object.values(enums.ITEM_CATEGORIES), "Invalid category")
    .required("Category is required"),
  purchasePrice: yup
    .number()
    .typeError("Purchase Price must be a number")
    .positive("Purchase Price must be greater than 0")
    .required("Purchase Price is required"),
  sellingPrice: yup
    .number()
    .typeError("Selling Price must be a number")
    .positive("Selling Price must be greater than 0")
    .required("Selling Price is required"),
  warrantyPeriodMonths: yup
    .number()
    .typeError("Warranty Period must be a number")
    .min(0, "Warranty Period must be at least 0 months")
    .required("Warranty Period is required"),
};

export const itemUpdateSchema = yup.object().shape({
  ...commonItemUpdateSchema,
  attributes: yup.mixed().when("category", {
    is: enums.ITEM_CATEGORIES.LENSE,
    then: () => lenseAttributesSchema,
    otherwise: (schema) =>
      schema.when("category", {
        is: enums.ITEM_CATEGORIES.FRAME,
        then: () => frameAttributesSchema,
        otherwise: (schema) =>
          schema.when("category", {
            is: enums.ITEM_CATEGORIES.SUNGLASS,
            then: () => sunglassAttributesSchema,
            otherwise: (schema) =>
              schema.when("category", {
                is: enums.ITEM_CATEGORIES.ACCESSORIES,
                then: () => accessoriesAttributesSchema,
                otherwise: (schema) => schema.notRequired(),
              }),
          }),
      }),
  }),
});
